<template>
  <div class="timer-wrap">
    <div class="timer">
      <div class="countdown">
        <div class="callout">
          <div class="price">
            {{ formattedDays }}
          </div>

          <small>Days</small>
        </div>

        <div class="timer-content">
          <h2>{{ eventName }} bookings ongoing</h2>

          <div class="numbers">
            <div class="time-unit hours">
              <span>{{ formattedHours }}</span>
            </div>

            <span class="separator">:</span>

            <div class="time-unit minutes">
              <span>{{ formattedMinutes }}</span>
            </div>

            <span class="separator">:</span>

            <div class="time-unit seconds">
              <span>{{ formattedSeconds }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
const SECOND = 1000;
const MINUTE = 60 * SECOND;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

export default {
  name: "EventCountdown",

  props: {
    startDate: {
      type: [Date, String, Number],
      default: () => new Date(),
    },

    /**
     * When true, the countdown continues after reaching zero
     * and displays the elapsed time with a negative sign.
     */
    negative: {
      type: Boolean,
      default: false,
    },

    eventName: {
      type: String,
      default: "Event",
    },
  },

  data() {
    return {
      now: Date.now(),
      timer: null,
      hasEnded: false,
    };
  },

  computed: {
    targetTime() {
      const date = this.toDate(this.startDate);

      return date ? date.getTime() : null;
    },

    /**
     * Signed difference between the target date and now.
     *
     * Positive  = time remaining
     * Negative  = event has already passed
     */
    remainingMilliseconds() {
      if (this.targetTime === null) {
        return 0;
      }

      return this.targetTime - this.now;
    },

    /**
     * Absolute duration used for formatting.
     */
    duration() {
      return Math.abs(this.remainingMilliseconds);
    },

    days() {
      return Math.floor(this.duration / DAY);
    },

    hours() {
      return Math.floor((this.duration % DAY) / HOUR);
    },

    minutes() {
      return Math.floor((this.duration % HOUR) / MINUTE);
    },

    seconds() {
      return Math.floor((this.duration % MINUTE) / SECOND);
    },

    formattedDays() {
      return this.formatNumber(this.days);
    },

    formattedHours() {
      return this.formatNumber(this.hours);
    },

    formattedMinutes() {
      return this.formatNumber(this.minutes);
    },

    formattedSeconds() {
      return this.formatNumber(this.seconds);
    },
  },

  watch: {
    startDate: {
      immediate: true,

      handler() {
        this.resetCountdown();
      },
    },
  },

  mounted() {
    this.startCountdown();
  },

  beforeDestroy() {
    this.stopCountdown();
  },

  methods: {
    toDate(value) {
      if (value instanceof Date) {
        return Number.isNaN(value.getTime()) ? null : value;
      }

      const date = new Date(value);

      return Number.isNaN(date.getTime()) ? null : date;
    },

    formatNumber(value) {
      return String(value).padStart(2, "0");
    },

    startCountdown() {
      this.stopCountdown();

      this.updateNow();
      this.checkForEnd();

      /**
       * Aligning the interval to one second is sufficient for
       * a visual countdown. The actual target timestamp remains
       * the source of truth, so timer drift doesn't accumulate.
       */
      this.timer = setInterval(() => {
        this.updateNow();
        this.checkForEnd();
      }, SECOND);
    },

    stopCountdown() {
      if (this.timer !== null) {
        clearInterval(this.timer);
        this.timer = null;
      }
    },

    resetCountdown() {
      this.hasEnded = false;
      this.updateNow();

      if (this.$el) {
        this.startCountdown();
      }
    },

    updateNow() {
      this.now = Date.now();
    },

    checkForEnd() {
      if (this.negative || this.targetTime === null) {
        return;
      }

      if (this.remainingMilliseconds <= 0 && !this.hasEnded) {
        this.hasEnded = true;
        this.stopCountdown();

        this.$emit("endTime");
      }
    },
  },
};
</script>

<style scoped>
.timer-wrap {
  width: 100%;
}

.timer {
  position: relative;
  width: 100%;
  background: #fff;
  border-radius: 0.5rem;
}

.countdown {
  position: relative;
  display: flex;
  flex-direction: column;
  font-family: "PT Sans Caption", sans-serif;
}

.timer-content {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 1rem;
  padding-right: 13rem;
}

.timer h2 {
  margin: 0;
  color: #000;
  font-size: 1rem;
  font-weight: 600;
}

.view {
  margin-top: 0.5rem;
  color: inherit;
  text-decoration: none;
}

.view span {
  display: inline-block;
  padding: 0.25rem 0.5rem;
  color: #fff;
  background: var(--v-primary-base);
  border-radius: 0.25rem;
}

.numbers {
  display: flex;
  align-items: baseline;
  margin-top: 0.75rem;
}

.time-unit {
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fff;
  box-shadow: inset 0 0 3px #f4f5fa;
  line-height: 1;
  overflow: hidden;
}

.time-unit.hours {
  font-size: 4rem;
}

.time-unit.minutes {
  font-size: 3rem;
}

.time-unit.seconds {
  font-size: 2rem;
}

.separator {
  margin: 0 0.4rem;
  font-size: 2.5rem;
  line-height: 1;
}

.callout {
  position: absolute;
  top: 0.75rem;
  right: 0.75rem;
  z-index: 1;

  display: flex;
  align-items: center;
  justify-content: space-between;

  width: 12rem;
  padding: 0.5rem 0.75rem;

  color: #f7f7f7;
  background: var(--v-primary-base);
  border-radius: 15px;

  font-size: 1.3rem;
  text-align: center;
}

.callout .price {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
  font-weight: 600;
}

.callout small {
  font-size: 0.8rem;
}

@media only screen and (max-width: 480px) {
  .timer {
    padding: 1rem;
  }

  .timer-content {
    align-items: flex-start;
    padding: 0;
    padding-top: 5rem;
  }

  .timer h2 {
    font-size: 0.9rem;
    font-weight: 400;
  }

  .callout {
    top: 0.75rem;
    right: 0.75rem;

    width: 7rem;
    height: 4rem;

    border-radius: 2rem;

    font-size: 0.8rem;
  }

  .callout .price {
    font-size: 1.5rem;
  }

  .numbers {
    width: 100%;
    justify-content: center;
  }

  .time-unit.hours {
    font-size: 3rem;
  }

  .time-unit.minutes {
    font-size: 2.5rem;
  }

  .time-unit.seconds {
    font-size: 1.75rem;
  }

  .separator {
    margin: 0 0.25rem;
    font-size: 2rem;
  }
}
</style>
